import { useEffect, useState, useCallback } from "react";
import { getAdminSystemWallet } from "../../services/ingroove/adminService";
import PageHeader from "../../components/common/PageHeader";
import WalletStatCards from "../../components/ingroove/wallet/WalletStatCards";
import TransactionsTable from "../../components/ingroove/wallet/TransactionsTable";
import {
  Search,
  SlidersHorizontal,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const SystemWallet = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [totalPages, setTotalPages] = useState(1);

  const hasActiveFilters = fromDate || toDate;
  const pageSize = 30;

  const fetchWallet = useCallback(async (searchVal, pageVal, from, to) => {
    setLoading(true);
    try {
      const res = await getAdminSystemWallet({
        search: searchVal,
        pageNumber: pageVal,
        pageSize,
        fromDate: from || undefined,
        toDate: to || undefined,
      });
      setData(res);
      // Estimate total pages from transaction count if available
      if (res?.recentTransactions?.length === pageSize) {
        setTotalPages(pageVal + 1);
      } else {
        setTotalPages(pageVal);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(1);
      fetchWallet(search, 1, fromDate, toDate);
    }, 400);
    return () => clearTimeout(timeout);
  }, [search, fromDate, toDate]);

  useEffect(() => {
    fetchWallet(search, page, fromDate, toDate);
  }, [page]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="System Wallet"
        subtitle="Ingroove platform wallet — all fees collected and platform payouts"
      />

      {/* Stat cards */}
      {!loading && data && <WalletStatCards data={data} />}
      {loading && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="bg-bg-surface border border-border rounded-xl h-24 animate-pulse"
            />
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
          />
          <input
            type="text"
            placeholder="Search by name or reference..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm bg-bg-surface border border-border rounded-lg text-text-primary placeholder-text-hint focus:outline-none focus:border-primary transition"
          />
        </div>

        <button
          onClick={() => setShowFilters((p) => !p)}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs transition ${
            showFilters || hasActiveFilters
              ? "border-primary text-primary bg-primary/10"
              : "border-border text-text-muted hover:border-border-hover"
          }`}
        >
          <SlidersHorizontal size={13} />
          Date
          {hasActiveFilters && (
            <span className="w-1.5 h-1.5 rounded-full bg-primary" />
          )}
        </button>

        {hasActiveFilters && (
          <button
            onClick={() => {
              setFromDate("");
              setToDate("");
            }}
            className="flex items-center gap-1 text-xs text-text-muted hover:text-red-400 transition"
          >
            <X size={12} /> Clear
          </button>
        )}
      </div>

      {showFilters && (
        <div className="bg-bg-surface border border-border rounded-xl p-4 flex flex-wrap gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-text-muted uppercase tracking-wide">
              From
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setPage(1);
              }}
              className="text-sm bg-bg-base border border-border rounded-lg px-3 py-1.5 text-text-primary focus:outline-none focus:border-primary transition"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-text-muted uppercase tracking-wide">
              To
            </label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setPage(1);
              }}
              className="text-sm bg-bg-base border border-border rounded-lg px-3 py-1.5 text-text-primary focus:outline-none focus:border-primary transition"
            />
          </div>
        </div>
      )}

      {/* Transactions table */}
      <TransactionsTable
        transactions={data?.recentTransactions ?? []}
        loading={loading}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-text-muted">
          <span>Page {page}</span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded-lg border border-border hover:border-primary hover:text-primary transition disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={data?.recentTransactions?.length < pageSize}
              className="p-1.5 rounded-lg border border-border hover:border-primary hover:text-primary transition disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemWallet;
